/**
 * Library Detail Page
 * 
 * Phase C3 - Read-only document inspection view
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, Button, Card, Badge, Divider } from '@threadlock/ui';
import { ROUTES } from '@threadlock/routing';
import { getDocument, listJobsForDocument, type RbDocument, type RbJob } from '@threadlock/data';
import { db } from '../../firebase-init';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

function getStatusColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'ready':
      return 'success';
    case 'processing':
    case 'uploaded':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

function formatDate(timestamp: { toDate: () => Date }): string {
  return timestamp.toDate().toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function LibraryDetailPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<RbDocument | null>(null);
  const [jobs, setJobs] = useState<RbJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    async function loadDocument() {
      if (!documentId) {
        setLoading(false);
        return;
      }

      try {
        const doc = await getDocument(db, documentId);
        if (!doc) {
          navigate(ROUTES.NOT_FOUND);
          return;
        }
        setDocument(doc);

        // Load jobs
        const jobList = await listJobsForDocument(db, documentId);
        setJobs(jobList);

        // Generate download URL
        try {
          const storage = getStorage();
          const fileRef = ref(storage, doc.storagePath);
          const url = await getDownloadURL(fileRef);
          setDownloadUrl(url);
        } catch (err) {
          console.error('Failed to generate download URL:', err);
        }
      } catch (err) {
        console.error('Failed to load document:', err);
      } finally {
        setLoading(false);
      }
    }

    loadDocument();
  }, [documentId, navigate]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center text-[var(--surface-text-muted)]">Loading document...</div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center text-[var(--status-error)]">Document not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title={document.filename}
        description={`Document ID: ${document.id}`}
        actions={
          <Button variant="secondary" onClick={() => navigate(ROUTES.LIBRARY)}>
            Back to Library
          </Button>
        }
      />

      <div className="mt-8 space-y-6">
        {/* Metadata Card */}
        <Card>
          <h2 className="text-[var(--text-lg)] font-semibold text-[var(--surface-text-primary)] mb-4">
            Metadata
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[var(--text-sm)] text-[var(--surface-text-secondary)] mb-1">
                Document Type
              </div>
              <div className="text-[var(--text-base)] text-[var(--surface-text-primary)]">
                {document.documentType}
              </div>
            </div>
            <div>
              <div className="text-[var(--text-sm)] text-[var(--surface-text-secondary)] mb-1">
                Jurisdiction
              </div>
              <div className="text-[var(--text-base)] text-[var(--surface-text-primary)]">
                {document.jurisdiction}
              </div>
            </div>
            <div>
              <div className="text-[var(--text-sm)] text-[var(--surface-text-secondary)] mb-1">
                Policy Tier
              </div>
              <div className="text-[var(--text-base)] text-[var(--surface-text-primary)]">
                {document.policyTier}
              </div>
            </div>
            <div>
              <div className="text-[var(--text-sm)] text-[var(--surface-text-secondary)] mb-1">
                Content Type
              </div>
              <div className="text-[var(--text-base)] text-[var(--surface-text-primary)]">
                {document.contentType === 'application/pdf' ? 'PDF' : 'DOCX'}
              </div>
            </div>
            <div>
              <div className="text-[var(--text-sm)] text-[var(--surface-text-secondary)] mb-1">
                Created
              </div>
              <div className="text-[var(--text-base)] text-[var(--surface-text-primary)]">
                {formatDate(document.createdAt)}
              </div>
            </div>
            <div>
              <div className="text-[var(--text-sm)] text-[var(--surface-text-secondary)] mb-1">
                Updated
              </div>
              <div className="text-[var(--text-base)] text-[var(--surface-text-primary)]">
                {formatDate(document.updatedAt)}
              </div>
            </div>
          </div>
        </Card>

        {/* Status Card */}
        <Card>
          <h2 className="text-[var(--text-lg)] font-semibold text-[var(--surface-text-primary)] mb-4">
            Status
          </h2>
          <div className="flex items-center gap-3">
            <Badge variant={getStatusColor(document.status)}>
              {document.status}
            </Badge>
            {document.errorMessage && (
              <div className="text-[var(--text-sm)] text-[var(--status-error)]">
                {document.errorMessage}
              </div>
            )}
          </div>
          {downloadUrl && (
            <div className="mt-4">
              <Button
                variant="secondary"
                onClick={() => window.open(downloadUrl, '_blank')}
              >
                Download Raw File
              </Button>
            </div>
          )}
        </Card>

        {/* Ingestion History Card */}
        <Card>
          <h2 className="text-[var(--text-lg)] font-semibold text-[var(--surface-text-primary)] mb-4">
            Ingestion History
          </h2>
          {jobs.length === 0 ? (
            <div className="text-[var(--text-sm)] text-[var(--surface-text-muted)]">
              No history available
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job, index) => (
                <div key={job.id}>
                  {index > 0 && <Divider />}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[var(--text-sm)] font-medium text-[var(--surface-text-primary)]">
                        {job.action.replace(/_/g, ' ')}
                      </div>
                      {job.message && (
                        <div className="text-[var(--text-sm)] text-[var(--surface-text-secondary)] mt-1">
                          {job.message}
                        </div>
                      )}
                      {job.meta?.errorCode && (
                        <div className="text-[var(--text-xs)] text-[var(--status-error)] mt-1">
                          Error: {job.meta.errorCode}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge variant={job.status === 'ok' ? 'success' : 'error'}>
                        {job.status}
                      </Badge>
                      <div className="text-[var(--text-xs)] text-[var(--surface-text-muted)] mt-1">
                        {formatDate(job.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
